import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CalendarClock, CalendarDays, UserCheck, Copy, HelpCircle, Users, Trophy, Award, Euro, BookOpen, Repeat, Info, Package, Mail, Phone, Activity,
} from "lucide-react";
import {
  Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { InfoHint } from "@/components/InfoHint";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Legend,
} from "recharts";

const eur = (v) => "€ " + Math.round(v ?? 0).toLocaleString("it-IT");

const KPI_META = {
  today: { label: "Ricevuti oggi", icon: CalendarClock, color: "text-blue-700", bg: "bg-blue-50",
    help: "Nuove richieste arrivate OGGI (fuso orario Europe/Rome)." },
  last7: { label: "Ultimi 7 giorni", icon: CalendarDays, color: "text-violet-700", bg: "bg-violet-50",
    help: "Richieste arrivate negli ultimi 7 giorni." },
  this_month: { label: "Questo mese", icon: CalendarDays, color: "text-indigo-700", bg: "bg-indigo-50",
    help: "Richieste arrivate dal 1° del mese corrente a oggi." },
  this_year: { label: "Quest'anno", icon: Users, color: "text-emerald-700", bg: "bg-emerald-50",
    help: "Totale richieste dell'anno corrente." },
  last_year: { label: "Anno scorso", icon: Users, color: "text-amber-700", bg: "bg-amber-50",
    help: "Totale richieste dell'anno precedente." },
  two_years_ago: { label: "2 anni fa", icon: Users, color: "text-slate-700", bg: "bg-slate-100",
    help: "Totale richieste di due anni fa." },
};

const KPI_GROUPS = [
  { title: "Volume richieste", keys: ["today", "last7", "this_month"] },
  { title: "Totale per anno", keys: ["this_year", "last_year", "two_years_ago"] },
];

const TREND_OPTIONS = [
  { id: "7", label: "Settimana", title: "Andamento giornaliero (7 giorni)" },
  { id: "14", label: "14 giorni", title: "Andamento giornaliero (14 giorni)" },
  { id: "30", label: "Mese", title: "Andamento giornaliero (30 giorni)" },
  { id: "year", label: "Anno", title: "Andamento mensile (12 mesi)" },
  { id: "sempre", label: "Sempre", title: "Andamento mensile (tutto lo storico)" },
];

// Tiene i primi `n` e raggruppa il resto in "Altre" per grafici leggibili.
function capList(list, n = 12) {
  if (!list || list.length <= n) return list || [];
  const top = list.slice(0, n);
  const rest = list.slice(n).reduce((s, r) => s + (r.count || 0), 0);
  if (rest > 0) top.push({ name: "Altre", count: rest });
  return top;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [trend, setTrend] = useState("14");
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", trend],
    queryFn: async () => (await api.get("/dashboard/stats", { params: { trend } })).data,
  });

  const trendTitle = TREND_OPTIONS.find((t) => t.id === trend)?.title || "Andamento";
  const trendTotal = (data?.trend || []).reduce((s, p) => s + (p.count || 0), 0);
  const trendPrevTotal = (data?.trend || []).reduce((s, p) => s + (p.prev || 0), 0);
  const trendDelta = trendPrevTotal > 0 ? Math.round(((trendTotal - trendPrevTotal) / trendPrevTotal) * 100) : null;
  const spendTotal = (data?.trend || []).reduce((s, p) => s + (p.spend || 0), 0);
  const spendPrevTotal = (data?.trend || []).reduce((s, p) => s + (p.spend_prev || 0), 0);
  const hasSpend = (data?.trend || []).some((p) => p.spend != null);
  const prevRange = (() => {
    const t = data?.trend || [];
    const from = t[0]?.prev_date, to = t[t.length - 1]?.prev_date;
    return from && to ? `${from}–${to}` : null;
  })();
  const courseData = useMemo(() => capList(data?.by_course), [data]);
  const professionData = useMemo(() => capList(data?.by_profession), [data]);
  const chartHeight = (rows) => Math.max(288, (rows?.length || 0) * 30);
  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* KPI cards: raggruppate e con legenda "i" per chiarezza */}
      <Accordion type="multiple" defaultValue={[]} className="space-y-3">
        {KPI_GROUPS.map((g) => (
          <AccordionItem key={g.title} value={g.title} className="rounded-lg border bg-white px-5 shadow-sm" data-testid={`section-${g.title}`}>
            <AccordionTrigger className="text-sm font-semibold uppercase tracking-wide text-slate-500">{g.title}</AccordionTrigger>
            <AccordionContent>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {g.keys.map((key) => {
                const kpi = KPI_META[key];
                const Icon = kpi.icon;
                const yr = data?.kpi_years?.[key];
                const label = yr ? String(yr) : kpi.label;
                const prev = data?.volume_prev?.[key];
                return (
                  <Card key={key} className="border-border shadow-sm" data-testid={`kpi-${key}`}>
                    <CardContent className="p-5">
                      <div className="mb-3 flex items-center justify-between">
                        <div className={`inline-flex h-9 w-9 items-center justify-center rounded-md ${kpi.bg}`}>
                          <Icon className={`h-5 w-5 ${kpi.color}`} />
                        </div>
                        <InfoHint testid={`kpi-info-${key}`} label={`Info ${kpi.label}`} text={kpi.help} />
                      </div>
                      {isLoading ? (
                        <Skeleton className="h-8 w-16" />
                      ) : (
                        <div className="flex items-baseline gap-2">
                          <p className="font-display text-3xl font-bold tracking-tight">{data?.[key] ?? 0}</p>
                          {prev != null && (
                            <span className="text-sm font-semibold text-muted-foreground" data-testid={`kpi-${key}-prev`}>prec. {prev.toLocaleString("it-IT")}</span>
                          )}
                        </div>
                      )}
                      <p className="mt-1 text-xs font-medium text-slate-600">{label}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            </AccordionContent>
          </AccordionItem>
        ))}

      {/* Clienti e ordini: clienti = persone distinte con acquisto; ordini = affari vinti */}
      <AccordionItem value="clienti" className="rounded-lg border bg-white px-5 shadow-sm" data-testid="customers-section">
        <AccordionTrigger className="text-sm font-semibold uppercase tracking-wide text-slate-500">Clienti e ordini</AccordionTrigger>
        <AccordionContent>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard testid="customers-month" icon={UserCheck} color="text-teal-700" bg="bg-teal-50"
            label="Clienti questo mese" value={data?.customers?.customers_month}
            prev={data?.customers?.customers_month_prev} prevLabel="mese precedente alla stessa data"
            prevFull={data?.customers?.customers_month_prev_full} prevFullLabel="mese prec. intero" loading={isLoading}
            help="Persone DISTINTE con almeno un acquisto (affare vinto) in questo mese. 'prec.' = mese precedente ALLA STESSA DATA (es. oggi è il 15 → confronto al 15 del mese scorso); 'mese prec. intero' = totale del mese precedente completo." />
          <StatCard testid="customers-year" icon={UserCheck} color="text-emerald-700" bg="bg-emerald-50"
            label="Clienti quest'anno" value={data?.customers?.customers_year}
            prev={data?.customers?.customers_year_prev} prevLabel="anno scorso a pari data" loading={isLoading}
            help="Persone DISTINTE con almeno un acquisto (affare vinto) nell'anno corrente. 'prec.' = anno scorso alla stessa data." />
          <StatCard testid="orders-month" icon={Trophy} color="text-amber-700" bg="bg-amber-50"
            label="Ordini questo mese" value={data?.customers?.orders_month}
            prev={data?.customers?.orders_month_prev} prevLabel="mese precedente alla stessa data"
            prevFull={data?.customers?.orders_month_prev_full} prevFullLabel="mese prec. intero" loading={isLoading}
            help="Numero di affari VINTI in questo mese (un cliente può avere più ordini). 'prec.' = mese precedente ALLA STESSA DATA (es. oggi è il 15 → confronto al 15 del mese scorso); 'mese prec. intero' = totale del mese precedente completo." />
          <StatCard testid="orders-year" icon={Trophy} color="text-orange-700" bg="bg-orange-50"
            label="Ordini quest'anno" value={data?.customers?.orders_year}
            prev={data?.customers?.orders_year_prev} prevLabel="anno scorso a pari data" loading={isLoading}
            help="Numero di affari VINTI nell'anno corrente (un cliente può avere più ordini). 'prec.' = anno scorso alla stessa data." />
          <StatCard testid="new-customers-year" icon={Users} color="text-sky-700" bg="bg-sky-50"
            label="Nuovi clienti (anno)" value={data?.customers?.new_customers_year}
            prev={data?.customers?.new_customers_year_prev} prevLabel="anno scorso a pari data" loading={isLoading}
            help="Clienti al PRIMO acquisto in assoluto, avvenuto quest'anno. 'prec.' = anno scorso alla stessa data." />
          <StatCard testid="returning-customers-year" icon={Repeat} color="text-fuchsia-700" bg="bg-fuchsia-50"
            label="Clienti di ritorno (anno)" value={data?.customers?.returning_customers_year}
            prev={data?.customers?.returning_customers_year_prev} prevLabel="anno scorso a pari data" loading={isLoading}
            help="Clienti che avevano GIÀ acquistato in passato e hanno RICOMPRATO quest'anno. 'prec.' = anno scorso alla stessa data." />
        </div>
        </AccordionContent>
      </AccordionItem>

      {/* Conto economico: fatturato vs spesa marketing + margine, mese e anno, con confronto */}
      <AccordionItem value="pnl" className="rounded-lg border bg-white px-5 shadow-sm" data-testid="pnl-section">
        <AccordionTrigger className="text-sm font-semibold uppercase tracking-wide text-slate-500">Conto economico</AccordionTrigger>
        <AccordionContent>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard testid="revenue-month" icon={Euro} color="text-emerald-700" bg="bg-emerald-50"
            label="Fatturato questo mese" value={data?.pnl?.revenue_month} prev={data?.pnl?.revenue_month_prev} prevLabel="mese precedente alla stessa data"
            prevFull={data?.pnl?.revenue_month_prev_full} prevFullLabel="mese prec. intero" fmt={eur} loading={isLoading}
            help="Somma dei valori dei deal VINTI questo mese (Pipedrive). 'prec.' = mese precedente ALLA STESSA DATA; 'mese prec. intero' = totale del mese precedente completo." />
          <StatCard testid="spend-month" icon={Euro} color="text-rose-700" bg="bg-rose-50"
            label="Spesa marketing (mese)" value={data?.pnl?.spend_month} prev={data?.pnl?.spend_month_prev} prevLabel="mese precedente alla stessa data"
            prevFull={data?.pnl?.spend_month_prev_full} prevFullLabel="mese prec. intero" fmt={eur} loading={isLoading}
            help="Spesa Meta di questo mese. 'prec.' = mese precedente ALLA STESSA DATA; 'mese prec. intero' = totale del mese precedente completo." />
          <StatCard testid="margin-month" icon={Award} color="text-indigo-700" bg="bg-indigo-50"
            label="Margine (mese)" value={data?.pnl?.margin_month} prev={data?.pnl?.margin_month_prev} prevLabel="mese precedente alla stessa data"
            prevFull={data?.pnl?.margin_month_prev_full} prevFullLabel="mese prec. intero" fmt={eur} loading={isLoading}
            help="Fatturato meno spesa marketing di questo mese. 'prec.' = mese precedente ALLA STESSA DATA; 'mese prec. intero' = totale del mese precedente completo." />
          <StatCard testid="revenue-year" icon={Euro} color="text-emerald-700" bg="bg-emerald-50"
            label="Fatturato quest'anno" value={data?.pnl?.revenue_year} prev={data?.pnl?.revenue_year_prev} prevLabel="anno scorso a pari data" fmt={eur} loading={isLoading}
            help="Somma dei valori dei deal VINTI nell'anno. 'prec.' = anno scorso alla stessa data." />
          <StatCard testid="spend-year" icon={Euro} color="text-rose-700" bg="bg-rose-50"
            label="Spesa marketing (anno)" value={data?.pnl?.spend_year} prev={data?.pnl?.spend_year_prev} prevLabel="anno scorso a pari data" fmt={eur} loading={isLoading}
            help="Spesa Meta dell'anno (fino a oggi). 'prec.' = anno scorso alla stessa data." />
          <StatCard testid="margin-year" icon={Award} color="text-indigo-700" bg="bg-indigo-50"
            label="Margine (anno)" value={data?.pnl?.margin_year} prev={data?.pnl?.margin_year_prev} prevLabel="anno scorso a pari data" fmt={eur} loading={isLoading}
            help="Fatturato meno spesa marketing dell'anno. 'prec.' = anno scorso alla stessa data." />
        </div>
        </AccordionContent>
      </AccordionItem>
      </Accordion>

      {/* Trend */}
      <Card className="shadow-sm">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <div className="flex items-center gap-4">
            <CardTitle className="font-display text-base tracking-tight">{trendTitle}</CardTitle>
            <div className="flex items-baseline gap-2" data-testid="trend-total">
              <span className="text-2xl font-bold text-primary">{isLoading ? "…" : trendTotal.toLocaleString("it-IT")}</span>
              <span className="text-xs uppercase tracking-wider text-muted-foreground">lead nel periodo</span>
              {trendPrevTotal > 0 && (
                <span className="flex items-baseline gap-1" data-testid="trend-prev-total" title="Totale del periodo precedente">
                  <span className="text-xl font-bold text-emerald-600">{trendPrevTotal.toLocaleString("it-IT")}</span>
                  <span className="text-xs uppercase tracking-wider text-emerald-600/80">periodo prec.{prevRange ? ` (${prevRange})` : ""}</span>
                </span>
              )}
              {trendDelta != null && (
                <span
                  data-testid="trend-delta"
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${trendDelta >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}
                  title={`Periodo precedente: ${trendPrevTotal.toLocaleString("it-IT")}`}
                >
                  {trendDelta >= 0 ? "+" : ""}{trendDelta}% vs prec.
                </span>
              )}
              {hasSpend && (
                <span className="ml-1 flex items-baseline gap-2 border-l border-border pl-3" data-testid="trend-spend-total">
                  <span className="text-lg font-bold text-[#dc2626]">€ {Math.round(spendTotal).toLocaleString("it-IT")}</span>
                  <span className="text-xs uppercase tracking-wider text-[#dc2626]/80">spesa Meta</span>
                  {spendPrevTotal > 0 && (
                    <span className="text-sm font-semibold text-[#dc2626]/70">prec. € {Math.round(spendPrevTotal).toLocaleString("it-IT")}</span>
                  )}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="trend-selector">
            {TREND_OPTIONS.map((t) => (
              <button
                key={t.id}
                data-testid={`trend-${t.id}`}
                onClick={() => setTrend(t.id)}
                className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                  trend === t.id
                    ? "bg-slate-900 text-white shadow-sm"
                    : "border border-border bg-secondary/40 text-muted-foreground hover:bg-secondary"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-72 w-full" />
          ) : (
            <ResponsiveContainer width="100%" height={288}>
              <LineChart data={data?.trend || []} margin={{ left: -20, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                {(data?.trend || []).some((p) => p.spend != null) && (
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#dc2626" }} axisLine={false} tickLine={false} tickFormatter={(v) => `€${Math.round(v / 1000)}k`} />
                )}
                <Tooltip content={<TrendTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {(data?.trend || []).some((p) => (p.prev || 0) > 0) && (
                  <Line yAxisId="left" type="monotone" dataKey="prev" stroke="#16a34a" strokeWidth={2} strokeDasharray="5 4" dot={false} activeDot={{ r: 4 }} name="Periodo precedente" />
                )}
                <Line yAxisId="left" type="monotone" dataKey="count" stroke="#1d4ed8" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} name="Periodo attuale" />
                {(data?.trend || []).some((p) => p.spend != null) && (
                  <Line yAxisId="right" type="monotone" dataKey="spend" stroke="#dc2626" strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="Spesa Meta (€)" connectNulls />
                )}
                {(data?.trend || []).some((p) => p.spend_prev != null) && (
                  <Line yAxisId="right" type="monotone" dataKey="spend_prev" stroke="#dc2626" strokeWidth={2} strokeDasharray="5 4" dot={false} activeDot={{ r: 3 }} name="Spesa Meta prec." connectNulls />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Vendite: fatturato per commerciale + corsi venduti, filtrabili per periodo */}
      <SalesSection />

      {/* Classifiche per commerciale (tipo di lead + vendite prodotto), filtrabili */}
      <RankingsSection />

      {/* Attività commerciale: email inviate, chiamate, attività Pipedrive */}
      <ActivitySection />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* By course */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="font-display text-base tracking-tight">Lead per corso</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-72 w-full" />
            ) : (courseData?.length ? (
              <ResponsiveContainer width="100%" height={chartHeight(courseData)}>
                <BarChart data={courseData} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11, fill: "#334155" }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }} />
                  <Bar dataKey="count" fill="#1d4ed8" radius={[0, 4, 4, 0]} name="Lead" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart />
            ))}
          </CardContent>
        </Card>

        {/* By profession */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="font-display text-base tracking-tight">Lead per professione</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-72 w-full" />
            ) : (professionData?.length ? (
              <ResponsiveContainer width="100%" height={chartHeight(professionData)}>
                <BarChart data={professionData} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11, fill: "#334155" }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }} />
                  <Bar dataKey="count" fill="#7c3aed" radius={[0, 4, 4, 0]} name="Lead" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart />
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, color, bg, label, value, prev, prevLabel, prevFull, prevFullLabel, loading, help, testid, fmt }) {
  const f = (v) => (fmt ? fmt(v) : (v ?? 0).toLocaleString("it-IT"));
  return (
    <Card className="border-border shadow-sm" data-testid={`kpi-${testid}`}>
      <CardContent className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className={`inline-flex h-9 w-9 items-center justify-center rounded-md ${bg}`}>
            <Icon className={`h-5 w-5 ${color}`} />
          </div>
          <InfoHint label={`Info ${label}`} text={help} />
        </div>
        {loading ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <div className="flex items-baseline gap-2">
            <p className="font-display text-3xl font-bold tracking-tight">{f(value)}</p>
            {prev != null && (
              <span className="text-sm font-semibold text-muted-foreground" title={prevLabel} data-testid={`kpi-${testid}-prev`}>
                prec. {f(prev)}
              </span>
            )}
          </div>
        )}
        {!loading && prevFull != null && (
          <p className="mt-0.5 text-xs text-slate-400" data-testid={`kpi-${testid}-prevfull`}>
            {prevFullLabel || "mese prec. intero"}: {f(prevFull)}
          </p>
        )}
        <p className="mt-1 text-xs font-medium text-slate-600">{label}</p>
      </CardContent>
    </Card>
  );
}

function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const cur = payload.find((p) => p.dataKey === "count");
  const prev = payload.find((p) => p.dataKey === "prev");
  const spend = payload.find((p) => p.dataKey === "spend");
  const spendPrev = payload.find((p) => p.dataKey === "spend_prev");
  const prevDate = payload[0]?.payload?.prev_date;
  return (
    <div className="rounded-lg border border-border bg-white p-3 text-[13px] shadow-md">
      <p className="mb-1 font-semibold text-slate-900">{label}</p>
      {cur && (
        <p className="flex items-center gap-2 text-[#1d4ed8]">
          <span className="inline-block h-2 w-2 rounded-full bg-[#1d4ed8]" /> Periodo attuale: <b>{cur.value}</b>
        </p>
      )}
      {prev && (
        <p className="flex items-center gap-2 text-[#16a34a]">
          <span className="inline-block h-2 w-2 rounded-full bg-[#16a34a]" /> Precedente{prevDate ? ` (${prevDate})` : ""}: <b>{prev.value}</b>
        </p>
      )}
      {spend && (
        <p className="flex items-center gap-2 text-[#dc2626]">
          <span className="inline-block h-2 w-2 rounded-full bg-[#dc2626]" /> Spesa Meta: <b>€ {Number(spend.value).toLocaleString("it-IT")}</b>
        </p>
      )}
      {spendPrev && (
        <p className="flex items-center gap-2 text-[#dc2626]">
          <span className="inline-block h-2 w-2 rounded-full border border-[#dc2626]" /> Spesa Meta prec.{prevDate ? ` (${prevDate})` : ""}: <b>€ {Number(spendPrev.value).toLocaleString("it-IT")}</b>
        </p>
      )}
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="flex h-72 items-center justify-center text-sm text-muted-foreground">
      Nessun dato disponibile. Sincronizza Gmail per importare i lead.
    </div>
  );
}

const RANK_MEDAL = ["bg-amber-100 text-amber-800", "bg-slate-200 text-slate-700", "bg-orange-100 text-orange-800"];

function RevenueRanking({ month, total, rows, isLoading }) {
  return (
    <Card className="shadow-sm" data-testid="revenue-ranking">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
          <Euro className="h-4 w-4 text-emerald-600" /> Fatturato per commerciale
        </CardTitle>
        <div className="flex items-center gap-2">
          {total && <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700" data-testid="revenue-total">Totale {total}</span>}
          {month && <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold text-muted-foreground" data-testid="revenue-month">{month}</span>}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-28 w-full" />
        ) : rows?.length ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {rows.map((r, i) => (
              <div key={r.commercial} className="flex items-center justify-between rounded-xl border border-border p-4" data-testid={`revenue-commercial-${i}`}>
                <div className="flex items-center gap-2.5">
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${RANK_MEDAL[i] || "bg-secondary text-muted-foreground"}`}>{i + 1}</span>
                  <div>
                    <p className="font-semibold text-slate-800">{r.commercial}</p>
                    <p className="text-xs text-muted-foreground">{r.sales} vendite</p>
                    {r.leads_count > 0 && (
                      <p className="mt-0.5 text-xs text-rose-600" data-testid={`revenue-lead-cost-${i}`}>
                        {r.leads_count} lead · costo {r.lead_cost_label}
                      </p>
                    )}
                    {r.realizzo_pct != null && (
                      <p className="mt-0.5 text-xs font-semibold text-blue-600" data-testid={`revenue-realizzo-${i}`}>
                        Realizzo {r.realizzo_pct}% ({r.sales}/{r.leads_count})
                      </p>
                    )}
                  </div>
                </div>
                <span className="font-display text-lg font-bold tabular-nums text-emerald-700">{r.revenue_label}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
            Nessuna vendita questo mese.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LeadTypesRanking({ month, rows, isLoading }) {
  return (
    <Card className="shadow-sm" data-testid="lead-types-ranking">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
          <Award className="h-4 w-4 text-amber-600" /> Classifica — tipo di lead per commerciale
        </CardTitle>
        {month && <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold text-muted-foreground" data-testid="ranking-month">{month}</span>}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : rows?.length ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {rows.map((r, i) => (
              <div key={r.commercial} className="rounded-xl border border-border p-4" data-testid={`ranking-commercial-${i}`}>
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${RANK_MEDAL[i] || "bg-secondary text-muted-foreground"}`}>{i + 1}</span>
                    <span className="font-semibold text-slate-800">{r.commercial}</span>
                  </div>
                  <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-bold text-blue-700">{r.total} lead</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {r.courses.map((c) => (
                    <span key={c.name} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs text-slate-700" title={`${c.name}: ${c.count}`}>
                      {c.name} <b className="text-slate-900">{c.count}</b>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
            Nessun lead inoltrato questo mese.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProductSalesRanking({ label, rows, isLoading }) {
  return (
    <Card className="shadow-sm" data-testid="product-sales-ranking">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
          <Package className="h-4 w-4 text-emerald-600" /> Classifica — vendite di prodotto per commerciale
        </CardTitle>
        {label && <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold text-muted-foreground" data-testid="product-ranking-period">{label}</span>}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : rows?.length ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {rows.map((r, i) => (
              <div key={r.commercial} className="rounded-xl border border-border p-4" data-testid={`product-commercial-${i}`}>
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${RANK_MEDAL[i] || "bg-secondary text-muted-foreground"}`}>{i + 1}</span>
                    <span className="font-semibold text-slate-800">{r.commercial}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-bold text-emerald-700">{r.total} vendite</span>
                    <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-bold text-slate-700">{r.revenue_label}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {r.products.map((p) => (
                    <span key={p.name} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs text-slate-700" title={`${p.name}: ${p.count} vendite · ${p.revenue_label}`}>
                      {p.name} <b className="text-slate-900">{p.count}</b>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
            Nessuna vendita nel periodo.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RankingsSection() {
  const now = new Date();
  const [mode, setMode] = useState("month");
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [year, setYear] = useState(String(now.getFullYear()));
  const params = mode === "month" ? { mode, month } : mode === "year" ? { mode, year } : { mode: "all" };
  const { data, isLoading } = useQuery({
    queryKey: ["rankings", mode, month, year],
    queryFn: async () => (await api.get("/analytics/rankings", { params })).data,
  });
  return (
    <div className="space-y-6" data-testid="rankings-section">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-secondary/30 p-3">
        <span className="text-sm font-semibold text-slate-700">Periodo classifiche</span>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger className="w-36 bg-white" data-testid="rankings-mode-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Mese per mese</SelectItem>
            <SelectItem value="year">Anno per anno</SelectItem>
            <SelectItem value="all">Sempre</SelectItem>
          </SelectContent>
        </Select>
        {mode === "month" && (
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-48 bg-white" data-testid="rankings-month-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              {monthOpts().map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {mode === "year" && (
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-32 bg-white" data-testid="rankings-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {yearOpts().map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      <LeadTypesRanking month={data?.period_label} rows={data?.lead_types_ranking} isLoading={isLoading} />
      <ProductSalesRanking label={data?.period_label} rows={data?.product_sales_ranking} isLoading={isLoading} />
    </div>
  );
}

const PD_TYPE_LABEL = { call: "Chiamate", richiamo: "Richiami", email: "Email", programmata: "Programmate", rinnovo: "Rinnovi", deadline: "Scadenze", lead_vecchi: "Lead vecchi", compleanno: "Compleanni" };
const MESI_ABBR = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
function fmtYM(ym) { const [y, m] = ym.split("-"); return `${MESI_ABBR[parseInt(m, 10) - 1]} ${y.slice(2)}`; }

function ActivitySection() {
  const [range, setRange] = useState("today");
  const [month, setMonth] = useState("none");
  const [day, setDay] = useState("");
  const params = day ? { day } : month !== "none" ? { month } : { range };
  const { data, isLoading } = useQuery({
    queryKey: ["activity", range, month, day],
    queryFn: async () => (await api.get("/analytics/activity", { params })).data,
  });
  const rows = data?.rows || [];
  const otherWord = day ? "giorno" : month !== "none" ? "mese" : (range === "today" || range === "yesterday") ? "giorno" : "periodo";
  return (
    <Card className="shadow-sm" data-testid="activity-section">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
          <Activity className="h-4 w-4 text-blue-700" /> Attività commerciale
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={range} onValueChange={(v) => { setRange(v); setMonth("none"); setDay(""); }}>
            <SelectTrigger className="w-40 bg-white" data-testid="activity-range-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Oggi</SelectItem>
              <SelectItem value="yesterday">Ieri</SelectItem>
              <SelectItem value="last7">Ultimi 7 giorni</SelectItem>
              <SelectItem value="last15">Ultimi 15 giorni</SelectItem>
              <SelectItem value="last30">Ultimo mese</SelectItem>
            </SelectContent>
          </Select>
          <Select value={month} onValueChange={(v) => { setMonth(v); setDay(""); }}>
            <SelectTrigger className="w-44 bg-white" data-testid="activity-month-select"><SelectValue placeholder="Un mese…" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="none">— Un mese specifico —</SelectItem>
              {monthOpts().map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <input
            type="date"
            value={day}
            onChange={(e) => setDay(e.target.value)}
            data-testid="activity-day-input"
            className="h-9 rounded-md border border-input bg-white px-2 text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {day && (
            <button type="button" onClick={() => setDay("")} data-testid="activity-day-clear" className="text-xs font-medium text-slate-500 underline hover:text-slate-800">
              azzera giorno
            </button>
          )}
          {data?.period_label && <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold text-muted-foreground" data-testid="activity-period">{data.period_label}</span>}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : rows.length ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {rows.map((r) => (
              <div key={r.commercial} className="rounded-xl border border-border p-4" data-testid={`activity-commercial-${r.commercial.replace(/\s/g, "-")}`}>
                <p className="mb-3 font-semibold text-slate-800">{r.commercial}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-sky-50 p-3">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-sky-700"><Mail className="h-3.5 w-3.5" /> Email inviate</div>
                    <p className="mt-1 font-display text-2xl font-bold tabular-nums text-sky-800" data-testid={`activity-emails-${r.commercial.replace(/\s/g, "-")}`}>{r.emails}</p>
                  </div>
                  <div className="rounded-lg bg-emerald-50 p-3">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-700"><Phone className="h-3.5 w-3.5" /> Chiamate</div>
                    <p className="mt-1 font-display text-2xl font-bold tabular-nums text-emerald-800" data-testid={`activity-calls-${r.commercial.replace(/\s/g, "-")}`}>{r.calls}</p>
                    {r.calls > 0 && <p className="text-[11px] text-emerald-600">{r.calls_answered} risposte</p>}
                  </div>
                </div>
                <div className="mt-3 rounded-lg border border-dashed border-border p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-600">Programmate ({data?.period_label}) per un altro {otherWord}</span>
                    <span className="font-bold tabular-nums text-slate-800">{r.pd_total}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {Object.entries(r.pd_by_type || {}).sort((a, b) => b[1] - a[1]).map(([t, n]) => (
                      <span key={t} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[11px] text-slate-700">{PD_TYPE_LABEL[t] || t} <b className="text-slate-900">{n}</b></span>
                    ))}
                    {r.pd_total === 0 && <span className="text-[11px] text-muted-foreground">Nessuna</span>}
                  </div>
                  {Object.keys(r.due_months || {}).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
                      <span className="font-medium">Da svolgere:</span>
                      {Object.entries(r.due_months).sort((a, b) => a[0].localeCompare(b[0])).map(([ym, n]) => (
                        <span key={ym}>{fmtYM(ym)} <b className="text-slate-700">{n}</b></span>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
                    <span className="text-xs font-medium text-slate-600">Da fare ({data?.period_label})</span>
                    {r.due_total > 0 ? (
                      <span className={`rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${r.due_done >= r.due_total ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid={`activity-due-${r.commercial.replace(/\s/g, "-")}`}>
                        {r.due_done}/{r.due_total} svolte
                      </span>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">Nessuna in scadenza</span>
                    )}
                  </div>
                  {r.due_activities !== undefined && (
                    <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`activity-due-breakdown-${r.commercial.replace(/\s/g, "-")}`}>
                      {r.due_fixed ? "Fisso (foto 23:59)" : "Live"}: {r.due_activities} attività + {r.due_requests} richieste
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">Nessuna attività nel periodo.</div>
        )}
        <p className="mt-3 text-xs text-muted-foreground">Email inviate = Gmail (caselle commerciali) + proposte inviate dal gestionale contratti.italiacorsi.it; chiamate da SalesTrail. "Programmate (…) per un altro giorno" = attività create nel periodo selezionato ma con scadenza in un altro giorno. "Da fare (…)" nel singolo giorno = numero FISSO = attività Pipedrive fotografate la sera prima alle 23:59 + richieste girate quel giorno (al netto di quelle rigirate ad altri); le "svolte" si aggiornano live (attività completate + richieste gestite). Nei periodi multipli il conteggio resta live. Esclusi gli inoltri interni dei lead.</p>
      </CardContent>
    </Card>
  );
}

const MESI_FULL = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function monthOpts() {
  const now = new Date();
  const out = [];
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: `${MESI_FULL[d.getMonth()]} ${d.getFullYear()}`,
    });
  }
  return out;
}

function yearOpts() {
  const y = new Date().getFullYear();
  const out = [];
  for (let i = y; i >= 2025; i--) out.push(String(i));
  return out;
}

function SalesSection() {
  const now = new Date();
  const [mode, setMode] = useState("month");
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [year, setYear] = useState(String(now.getFullYear()));
  const [dFrom, setDFrom] = useState("");
  const [dTo, setDTo] = useState("");
  const params = mode === "month" ? { mode, month }
    : mode === "year" ? { mode, year }
    : mode === "custom" ? { mode, date_from: dFrom, date_to: dTo }
    : { mode: "all" };
  const { data, isLoading } = useQuery({
    queryKey: ["sales", mode, month, year, dFrom, dTo],
    queryFn: async () => (await api.get("/analytics/sales", { params })).data,
    enabled: mode !== "custom" || (!!dFrom && !!dTo),
  });
  const qc = useQueryClient();
  const [dlg, setDlg] = useState(null);
  const { data: courses } = useQuery({
    queryKey: ["courses-min"],
    queryFn: async () => (await api.get("/courses")).data,
  });
  const dealsQ = useQuery({
    queryKey: ["sale-deals", mode, month, year, dFrom, dTo, dlg?.kind, dlg?.group],
    enabled: !!dlg,
    queryFn: async () => {
      const p = { ...params };
      if (dlg.kind === "renewals") p.renewals = true; else p.group = dlg.group;
      return (await api.get("/analytics/sales/deals", { params: p })).data;
    },
  });
  const assign = useMutation({
    mutationFn: async ({ deal_id, course_id }) =>
      (await api.post("/analytics/deals/assign", { deal_id, course_id })).data,
    onSuccess: () => {
      toast.success("Deal assegnato");
      qc.invalidateQueries({ queryKey: ["sales"] });
      qc.invalidateQueries({ queryKey: ["sale-deals"] });
    },
    onError: () => toast.error("Errore durante l'assegnazione"),
  });
  return (
    <div className="space-y-6" data-testid="sales-section">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-secondary/30 p-3">
        <span className="text-sm font-semibold text-slate-700">Periodo vendite</span>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger className="w-36 bg-white" data-testid="sales-mode-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Mese per mese</SelectItem>
            <SelectItem value="year">Anno per anno</SelectItem>
            <SelectItem value="custom">Personalizzato</SelectItem>
            <SelectItem value="all">Sempre</SelectItem>
          </SelectContent>
        </Select>
        {mode === "month" && (
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-48 bg-white" data-testid="sales-month-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              {monthOpts().map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {mode === "year" && (
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-32 bg-white" data-testid="sales-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {yearOpts().map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {mode === "custom" && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-slate-600">Da</span>
            <input type="date" value={dFrom} max={dTo || undefined} onChange={(e) => setDFrom(e.target.value)} data-testid="sales-date-from" className="h-9 rounded-md border border-input bg-white px-2 text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            <span className="text-xs font-medium text-slate-600">a</span>
            <input type="date" value={dTo} min={dFrom || undefined} onChange={(e) => setDTo(e.target.value)} data-testid="sales-date-to" className="h-9 rounded-md border border-input bg-white px-2 text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
        )}
      </div>
      <RevenueRanking month={data?.period_label} total={data?.revenue_total} rows={data?.revenue_ranking} isLoading={isLoading} />
      <RenewalsByCommercial label={data?.period_label} total={data?.renewals_total} count={data?.renewals_count} rows={data?.renewals_ranking} isLoading={isLoading} onCountClick={() => setDlg({ kind: "renewals" })} />
      <CoursesSold label={data?.period_label} total={data?.courses_total} sales={data?.courses_sales} rows={data?.courses_sold} isLoading={isLoading} onRowClick={(course) => setDlg({ kind: "group", group: course })} />

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-2xl" data-testid="deals-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {dlg?.kind === "renewals" ? "Rinnovi — dettaglio deal" : `${dlg?.group} — dettaglio deal`}
            </DialogTitle>
            <DialogDescription>{data?.period_label} · {dealsQ.data?.count ?? 0} deal</DialogDescription>
          </DialogHeader>
          <div className="max-h-[26rem] space-y-2 overflow-auto">
            {dealsQ.isLoading && <p className="py-6 text-center text-sm text-muted-foreground">Carico…</p>}
            {(dealsQ.data?.deals || []).map((x) => (
              <div key={x.deal_id} className="rounded-lg border border-border p-3" data-testid="deal-row">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium" title={x.title}>{x.title || "(senza titolo)"}</p>
                    <p className="text-xs text-muted-foreground">
                      {x.owner_name} · {x.won_at ? new Date(x.won_at).toLocaleDateString("it-IT", { timeZone: "Europe/Rome" }) : "—"}
                      {dlg?.kind === "renewals" ? ` · ${x.group}` : ""}
                    </p>
                  </div>
                  <span className="shrink-0 font-semibold tabular-nums text-emerald-700">{x.value_label}</span>
                </div>
                {dlg?.kind !== "renewals" && (
                  <div className="mt-2">
                    <Select onValueChange={(cid) => assign.mutate({ deal_id: x.deal_id, course_id: cid })}>
                      <SelectTrigger className="h-8 w-full" data-testid="deal-assign-select"><SelectValue placeholder="Assegna a un corso…" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {(courses || []).map((co) => <SelectItem key={co.id} value={co.id}>{co.official_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            ))}
            {!dealsQ.isLoading && (dealsQ.data?.deals || []).length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">Nessun deal.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CoursesSold({ label, total, sales, rows, isLoading, onRowClick }) {
  return (
    <Card className="shadow-sm" data-testid="courses-sold">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
          <BookOpen className="h-4 w-4 text-indigo-600" /> Corsi venduti
        </CardTitle>
        <div className="flex items-center gap-2">
          {typeof sales === "number" && <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700" data-testid="courses-sold-count">{sales} vendite</span>}
          {total && <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700" data-testid="courses-sold-total">Totale {total}</span>}
          {label && <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold text-muted-foreground">{label}</span>}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : rows?.length ? (
          <div className="max-h-[28rem] overflow-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-secondary/80 backdrop-blur">
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2">Corso</th>
                  <th className="px-4 py-2 text-right">Vendite</th>
                  <th className="px-4 py-2 text-right">Fatturato</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.course} onClick={() => onRowClick && onRowClick(r.course)} className="cursor-pointer border-t border-border hover:bg-secondary/40" data-testid="courses-sold-row">
                    <td className="px-4 py-2 font-medium text-slate-800">
                      {r.course === "Non classificato"
                        ? <span className="text-muted-foreground underline decoration-dotted">{r.course}</span>
                        : r.course}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{r.sales}</td>
                    <td className="px-4 py-2 text-right font-semibold tabular-nums text-emerald-700">{r.revenue_label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
            Nessuna vendita nel periodo selezionato.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RenewalsByCommercial({ label, total, count, rows, isLoading, onCountClick }) {
  return (
    <Card className="shadow-sm" data-testid="renewals-ranking">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
          <Repeat className="h-4 w-4 text-fuchsia-600" /> Rinnovi abbonamento per commerciale
        </CardTitle>
        <div className="flex items-center gap-2">
          {typeof count === "number" && <button type="button" onClick={onCountClick} className="rounded-full bg-fuchsia-50 px-2.5 py-1 text-xs font-bold text-fuchsia-700 transition-colors hover:bg-fuchsia-100" data-testid="renewals-count">{count} rinnovi ›</button>}
          {total && <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700" data-testid="renewals-total">Totale {total}</span>}
          {label && <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold text-muted-foreground">{label}</span>}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-28 w-full" />
        ) : rows?.length ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {rows.map((r, i) => (
              <div key={r.commercial} className="flex items-center justify-between rounded-xl border border-border p-4" data-testid={`renewals-commercial-${i}`}>
                <div className="flex items-center gap-2.5">
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${RANK_MEDAL[i] || "bg-secondary text-muted-foreground"}`}>{i + 1}</span>
                  <div>
                    <p className="font-semibold text-slate-800">{r.commercial}</p>
                    <p className="text-xs text-muted-foreground">{r.count} rinnovi</p>
                  </div>
                </div>
                <span className="font-display text-lg font-bold tabular-nums text-fuchsia-700">{r.revenue_label}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
            Nessun rinnovo nel periodo selezionato.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
